import { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const linkifyText = (text) => {
  const urlRegex = /(https?:\/\/[^\s<>[\]()]+)/g;
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  
  const combinedRegex = /(https?:\/\/[^\s<>[\]()]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  let match;
  
  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    const matchedText = match[0];
    if (match[1]) {
      parts.push(
        <a 
          key={key++} 
          href={matchedText} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-[#00E600] hover:underline break-all"
        >
          {matchedText}
        </a>
      );
    } else if (match[2]) {
      parts.push(
        <a 
          key={key++} 
          href={`mailto:${matchedText}`}
          className="text-[#00E600] hover:underline"
        >
          {matchedText}
        </a>
      );
    }
    
    lastIndex = match.index + matchedText.length;
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m the NYAXO Front-Desk Assistant. I can help you with information about our services, contact details, or answer questions about AI. How can I assist you today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    const updatedMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      const historyToSend = updatedMessages.slice(1).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: userMessage,
        session_id: sessionId,
        history: historyToSend.slice(-10)
      }, {
        timeout: 45000
      });

      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      const actionTaken = response.data.action_taken;
      const actionSuccess = response.data.action_success;
      
      let messageContent = response.data.response;
      let messageType = 'normal';
      
      if (actionTaken && actionSuccess) {
        messageType = 'action_success';
      } else if (actionTaken && !actionSuccess) {
        messageType = 'action_failed';
      }

      setMessages(prev => [
        ...prev,
        { 
          role: 'assistant', 
          content: messageContent,
          type: messageType,
          action: actionTaken
        }
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [
        ...prev,
        { 
          role: 'assistant', 
          content: 'I apologize, but I\'m having trouble connecting right now. Please try again or email us at office@nyaxo.net.' 
        }
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        data-testid="chatbot-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-full shadow-lg hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 flex items-center justify-center z-50 group"
      >
        {isOpen ? (
          <svg className="w-8 h-8 text-[#0D0D0D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-8 h-8 text-[#0D0D0D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#00E600] rounded-full animate-ping"></div>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chatbot-window"
          className="fixed bottom-28 right-8 w-96 h-[600px] bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg shadow-2xl flex flex-col z-50 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00E600] to-[#36E0A8] p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#0D0D0D] rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-[#00E600]" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-[#0D0D0D]">NYAXO AI Assistant</h3>
              <p className="text-xs text-[#0D0D0D]/80">Powered by Nyaxo Technologies Inc.</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chatbot-messages">
            {messages.map((message, index) => (
              <div
                key={index}
                data-testid={`chat-message-${message.role}`}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D]'
                      : message.type === 'action_success'
                      ? 'bg-[#0D0D0D] text-white border-2 border-[#00E600]'
                      : message.type === 'action_failed'
                      ? 'bg-[#0D0D0D] text-white border-2 border-orange-500'
                      : 'bg-[#0D0D0D] text-white border border-[#2F2F2F]'
                  }`}
                >
                  {message.type === 'action_success' && (
                    <div className="flex items-center space-x-2 mb-2 text-[#00E600] text-xs font-semibold">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{
                        message.action === 'book_consultation' ? 'Consultation Booked' :
                        message.action === 'submit_contact_form' ? 'Form Submitted' :
                        message.action === 'send_resources' ? 'Resources Sent' :
                        message.action === 'schedule_followup' ? 'Follow-up Scheduled' :
                        message.action === 'check_availability' ? 'Availability Checked' :
                        message.action === 'get_quote_estimate' ? 'Quote Generated' :
                        message.action === 'route_to_specialist' ? 'Specialist Found' :
                        'Action Completed'
                      }</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{linkifyText(message.content)}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start" data-testid="chatbot-loading">
                <div className="bg-[#0D0D0D] border border-[#2F2F2F] p-3 rounded-lg">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-[#00E600] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-[#00E600] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-[#00E600] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[#2F2F2F]">
            <div className="flex space-x-2 items-end">
              <textarea
                data-testid="chatbot-input"
                value={input}
                ref={inputRef}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything... (Shift+Enter for new line)"
                rows={1}
                className="flex-1 bg-[#0D0D0D] border border-[#2F2F2F] rounded-md px-4 py-2 text-white text-sm focus:border-[#00E600] focus:outline-none focus:ring-1 focus:ring-[#00E600] transition-colors resize-none min-h-[40px] max-h-[120px] overflow-y-auto"
                disabled={isLoading}
                style={{ height: 'auto', height: Math.min(120, Math.max(40, input.split('\n').length * 24)) + 'px' }}
              />
              <button
                data-testid="chatbot-send-button"
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                className="bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] p-2 rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;